import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Plus, Edit, Trash2, Phone, MapPin } from "lucide-react";
import ConfirmActionDialog from "@/components/ConfirmActionDialog";
import ProofImageDropzone from "@/components/ProofImageDropzone";
import ProofImageGallery from "@/components/ProofImageGallery";
import { PageHeader, PageShell } from "@/components/PageShell";
import { InlineHint, PageEmptyState, PageLoadingState } from "@/components/PageStates";
import { useAuth } from "@/contexts/AuthContext";
import { handleArrowFieldKeyDown } from "@/lib/keyboardNav";
import { buildProofImageSrc, createLocalProofImageEntries, isLikelyProofImageFile, normalizeProofImageUrls } from "@/lib/proofImages";
import { SUPPLIER_MANAGE_ROLES } from "@/lib/roles";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_SUPPLIER_TOKO_IMAGES = 5;
const MAX_SUPPLIER_TOKO_IMAGE_BYTES = 5 * 1024 * 1024;

const MasterSupplierPage = () => {
  const { user } = useAuth();
  const [supplier, setSupplier] = useState([]);
  const [kategori, setKategori] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({
    nama_supplier: "",
    kategori_ids: [],
    nik_npwp: "",
    nama_pemilik_toko: "",
    telepon: "",
    alamat: "",
    foto_toko_list: [],
    aktif: true,
  });
  const [newFotoTokoEntries, setNewFotoTokoEntries] = useState([]);
  const [currentId, setCurrentId] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: "",
    description: "",
    confirmText: "Ya",
    destructive: false,
    onConfirm: null,
  });
  const namaSupplierInputRef = useRef(null);
  const kategoriCheckboxRefs = useRef(new Map());
  const nikNpwpInputRef = useRef(null);
  const namaPemilikTokoInputRef = useRef(null);
  const teleponInputRef = useRef(null);
  const alamatTextareaRef = useRef(null);
  const aktifCheckboxRef = useRef(null);
  const supplierCancelButtonRef = useRef(null);
  const supplierSubmitButtonRef = useRef(null);
  const canManageSupplier = SUPPLIER_MANAGE_ROLES.includes(user?.role);

  const fetchData = useCallback(async (fallbackKategoriBySupplier = new Map()) => {
    try {
      const [supplierRes, kategoriRes] = await Promise.all([
        axios.get(`${API}/supplier`),
        axios.get(`${API}/kategori`),
      ]);

      const existingKategoriBySupplier = new Map([
        ...fallbackKategoriBySupplier.entries(),
      ]);

      const normalizedSupplier = supplierRes.data.map((item) => ({
        ...item,
        kategori_ids: Array.isArray(item.kategori_ids)
          ? item.kategori_ids
          : existingKategoriBySupplier.get(item.supplier_id) || [],
        foto_toko_list: normalizeProofImageUrls(item.foto_toko_list || item.foto_toko),
        aktif: item.aktif !== false,
      }));

      setSupplier(normalizedSupplier);
      setKategori(kategoriRes.data.filter((k) => k.aktif));
    } catch (error) {
      toast.error("Gagal memuat data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canManageSupplier) {
      toast.error("Akses pengelolaan supplier hanya untuk Kepala Ruangan");
      return;
    }

    setConfirmDialog({
      open: true,
      title: editMode ? "Konfirmasi Perbarui Supplier" : "Konfirmasi Simpan Supplier",
      description: editMode
        ? "Apakah Anda yakin ingin memperbarui data supplier ini?"
        : "Apakah Anda yakin ingin menyimpan data supplier ini?",
      confirmText: editMode ? "Ya, Perbarui" : "Ya, Simpan",
      destructive: false,
      onConfirm: async () => {
        try {
          const existingFotoTokoUrls = normalizeProofImageUrls(formData.foto_toko_list);
          const uploadedFotoTokoUrls = [];

          for (const fotoEntry of newFotoTokoEntries) {
            const fotoFormData = new FormData();
            fotoFormData.append("file", fotoEntry.file);
            const uploadRes = await axios.post(`${API}/upload/supplier-toko`, fotoFormData);
            uploadedFotoTokoUrls.push(uploadRes.data.url);
          }

          const submitPayload = {
            ...formData,
            nama_supplier: String(formData.nama_supplier || "").trim(),
            nama_pemilik_toko: String(formData.nama_pemilik_toko || "").trim() || null,
            nik_npwp: String(formData.nik_npwp || "").trim() || null,
            telepon: String(formData.telepon || "").trim() || null,
            alamat: String(formData.alamat || "").trim() || null,
            foto_toko_list: [...existingFotoTokoUrls, ...uploadedFotoTokoUrls],
          };

          if (editMode) {
            const response = await axios.put(`${API}/supplier/${currentId}`, submitPayload);
            const updatedSupplier = {
              ...response.data,
              kategori_ids: Array.isArray(response.data?.kategori_ids)
                ? response.data.kategori_ids
                : submitPayload.kategori_ids,
            };
            setSupplier((prev) =>
              prev.map((item) =>
                item.supplier_id === currentId ? updatedSupplier : item
              )
            );
            toast.success("Supplier berhasil diupdate");
            await fetchData(new Map([[currentId, updatedSupplier.kategori_ids]]));
          } else {
            const response = await axios.post(`${API}/supplier`, submitPayload);
            const createdSupplier = {
              ...response.data,
              kategori_ids: Array.isArray(response.data?.kategori_ids)
                ? response.data.kategori_ids
                : submitPayload.kategori_ids,
            };
            setSupplier((prev) => [createdSupplier, ...prev]);
            toast.success("Supplier berhasil ditambahkan");
            await fetchData(
              new Map([[createdSupplier.supplier_id, createdSupplier.kategori_ids]])
            );
          }
          setDialogOpen(false);
          resetForm();
        } catch (error) {
          toast.error(error.response?.data?.detail || "Gagal menyimpan data");
        }
      },
    });
  };

  const handleEdit = (item) => {
    if (!canManageSupplier) {
      toast.error("Akses pengelolaan supplier hanya untuk Kepala Ruangan");
      return;
    }

    const normalizedFotoTokoUrls = normalizeProofImageUrls(item.foto_toko_list || item.foto_toko);
    setFormData({
      nama_supplier: item.nama_supplier,
      kategori_ids: Array.isArray(item.kategori_ids) ? item.kategori_ids : [],
      nik_npwp: item.nik_npwp || "",
      nama_pemilik_toko: item.nama_pemilik_toko || "",
      telepon: item.telepon || "",
      alamat: item.alamat || "",
      foto_toko_list: normalizedFotoTokoUrls,
      aktif: item.aktif !== false,
    });
    setNewFotoTokoEntries([]);
    setCurrentId(item.supplier_id);
    setEditMode(true);
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!canManageSupplier) {
      toast.error("Akses pengelolaan supplier hanya untuk Kepala Ruangan");
      return;
    }

    setConfirmDialog({
      open: true,
      title: "Konfirmasi Hapus Supplier",
      description: "Apakah Anda yakin ingin menghapus supplier ini? Tindakan ini tidak dapat dibatalkan.",
      confirmText: "Ya, Hapus",
      destructive: true,
      onConfirm: async () => {
        try {
          await axios.delete(`${API}/supplier/${id}`);
          toast.success("Supplier berhasil dihapus");
          fetchData();
        } catch (error) {
          toast.error("Gagal menghapus supplier");
        }
      },
    });
  };

  const resetForm = () => {
    setFormData({
      nama_supplier: "",
      kategori_ids: [],
      nik_npwp: "",
      nama_pemilik_toko: "",
      telepon: "",
      alamat: "",
      foto_toko_list: [],
      aktif: true,
    });
    setNewFotoTokoEntries([]);
    setEditMode(false);
    setCurrentId(null);
  };

  const getKategoriNames = (kategoriIds = []) => {
    if (!Array.isArray(kategoriIds) || kategoriIds.length === 0) {
      return "Belum ada kategori";
    }

    const names = kategori
      .filter((k) => kategoriIds.includes(k.kategori_id))
      .map((k) => k.nama_kategori);

    return names.length > 0 ? names.join(", ") : "Belum ada kategori";
  };

  const removeExistingFotoTokoByUrl = useCallback((url) => {
    setFormData((prev) => ({
      ...prev,
      foto_toko_list: normalizeProofImageUrls(prev.foto_toko_list).filter((itemUrl) => itemUrl !== url),
    }));
  }, []);

  const removeNewFotoById = useCallback((fotoId) => {
    setNewFotoTokoEntries((prev) => prev.filter((entry) => entry.id !== fotoId));
  }, []);

  const handleFotoTokoFilesSelected = useCallback(
    async (files) => {
      const fileList = Array.from(files || []).filter(Boolean);
      if (fileList.length === 0) {
        return;
      }

      const imageFiles = fileList.filter(isLikelyProofImageFile);
      if (imageFiles.length === 0) {
        toast.error("Semua file foto toko harus berupa gambar");
        return;
      }

      if (imageFiles.length < fileList.length) {
        toast.warning("Beberapa file diabaikan karena bukan gambar");
      }

      const validSizeFiles = imageFiles.filter((file) => Number(file.size || 0) <= MAX_SUPPLIER_TOKO_IMAGE_BYTES);
      if (validSizeFiles.length === 0) {
        toast.error("Ukuran setiap foto maksimal 5MB");
        return;
      }

      if (validSizeFiles.length < imageFiles.length) {
        toast.warning("Beberapa foto diabaikan karena melebihi 5MB");
      }

      const currentCount = normalizeProofImageUrls(formData.foto_toko_list).length + newFotoTokoEntries.length;
      const remainingSlots = MAX_SUPPLIER_TOKO_IMAGES - currentCount;
      if (remainingSlots <= 0) {
        toast.error(`Maksimal ${MAX_SUPPLIER_TOKO_IMAGES} foto arsip per supplier`);
        return;
      }

      const nextFiles = validSizeFiles.slice(0, remainingSlots);
      if (nextFiles.length < validSizeFiles.length) {
        toast.warning(`Hanya ${remainingSlots} foto yang ditambahkan karena batas maksimal ${MAX_SUPPLIER_TOKO_IMAGES} foto.`);
      }

      try {
        const previewEntries = await createLocalProofImageEntries(nextFiles, "supplier-toko");
        setNewFotoTokoEntries((prev) => [...prev, ...previewEntries]);
      } catch (error) {
        toast.error(error?.message || "Gagal menyiapkan preview foto toko");
      }
    },
    [formData.foto_toko_list, newFotoTokoEntries.length]
  );

  const supplierFotoGalleryItems = useMemo(() => {
    const storedItems = normalizeProofImageUrls(formData.foto_toko_list).map((url, index) => {
      const imageSrc = buildProofImageSrc(url);
      return {
        id: `stored-${url}`,
        src: imageSrc,
        href: imageSrc,
        alt: `Foto toko tersimpan ${index + 1}`,
        label: `Tersimpan ${index + 1}`,
        onRemove: () => removeExistingFotoTokoByUrl(url),
      };
    });

    const draftItems = newFotoTokoEntries.map((entry, index) => ({
      id: entry.id,
      src: entry.previewUrl,
      href: entry.previewUrl,
      alt: `Foto toko baru ${index + 1}`,
      label: `Baru ${index + 1}`,
      onRemove: () => removeNewFotoById(entry.id),
    }));

    return [...storedItems, ...draftItems];
  }, [formData.foto_toko_list, newFotoTokoEntries, removeExistingFotoTokoByUrl, removeNewFotoById]);

  const setKategoriCheckboxRef = useCallback((kategoriId, node) => {
    if (!kategoriId) {
      return;
    }

    if (node) {
      kategoriCheckboxRefs.current.set(kategoriId, node);
      return;
    }

    kategoriCheckboxRefs.current.delete(kategoriId);
  }, []);

  const focusKategoriCheckboxByIndex = (index) => {
    const target = kategori[index];
    if (!target) {
      return;
    }

    kategoriCheckboxRefs.current.get(target.kategori_id)?.focus();
  };

  const focusFirstKategoriOrNik = () => {
    if (kategori.length > 0) {
      focusKategoriCheckboxByIndex(0);
      return;
    }

    nikNpwpInputRef.current?.focus();
  };

  const focusLastKategoriOrNama = () => {
    if (kategori.length > 0) {
      focusKategoriCheckboxByIndex(kategori.length - 1);
      return;
    }

    namaSupplierInputRef.current?.focus();
  };

  if (loading) {
    return <PageLoadingState label="Memuat master supplier" description="Mengambil daftar supplier beserta kategori yang terhubung." />;
  }

  return (
    <PageShell data-testid="master-supplier-page">
      <PageHeader
        eyebrow="Master Data"
        title="Master Supplier"
        description={canManageSupplier
          ? "Kelola profil supplier, nama pemilik toko, dan arsip bukti toko fisik untuk kebutuhan administrasi."
          : "Anda dapat melihat daftar supplier, kategori, dan arsip bukti toko fisik. Akses pengelolaan dibatasi untuk Kepala Ruangan."
        }
        actions={canManageSupplier ? (
          <Button
            data-testid="add-supplier-button"
            onClick={() => {
              resetForm();
              setDialogOpen(true);
            }}
            className="w-full sm:w-auto"
          >
            <Plus size={16} className="mr-2" />
            Tambah Supplier
          </Button>
        ) : (
          <Badge
            variant="outline"
            className="inline-flex items-center gap-2 border-sky-200 bg-sky-50 px-3 py-1.5 text-sky-800"
          >
            Mode lihat
          </Badge>
        )}
      />

      {!canManageSupplier ? (
        <InlineHint className="border-sky-200/80 bg-sky-50/70 text-sky-950">
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant="outline" className="border-sky-300 bg-white text-sky-800">
              Pengawas
            </Badge>
            <span>
              Anda sedang melihat Master Supplier dalam mode baca saja. Tombol tambah, edit, dan hapus disembunyikan.
            </span>
          </div>
        </InlineHint>
      ) : null}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {supplier.length === 0 ? (
          <div className="col-span-full">
            <PageEmptyState
              title="Belum ada supplier"
              description={canManageSupplier
                ? "Tambahkan supplier agar transaksi pembelian bisa dipetakan ke kategori dan kontak yang benar."
                : "Belum ada supplier aktif yang dapat ditampilkan."
              }
            />
          </div>
        ) : (
          supplier.map((item) => (
            <div
              key={item.supplier_id}
              data-testid={`supplier-card-${item.supplier_id}`}
              className="rounded-lg border bg-card p-6 shadow-sm hover:shadow-md transition-all"
            >
              <div className="flex items-start justify-between mb-4">
                <h3 className="font-manrope font-bold text-lg">
                  {item.nama_supplier}
                </h3>
                <span
                  className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    item.aktif
                      ? "bg-green-100 text-green-800"
                      : "bg-gray-100 text-gray-800"
                  }`}
                >
                  {item.aktif ? "Aktif" : "Nonaktif"}
                </span>
              </div>

              <div className="space-y-2 mb-4">
                <p className="text-sm text-muted-foreground">
                  Kategori: {getKategoriNames(item.kategori_ids)}
                </p>
                {item.nik_npwp && (
                  <p className="text-sm text-muted-foreground">
                    NIK/NPWP: {item.nik_npwp}
                  </p>
                )}
                {item.telepon && (
                  <div className="flex items-center gap-2 text-sm">
                    <Phone size={14} className="text-muted-foreground" />
                    <span>{item.telepon}</span>
                  </div>
                )}
                {item.alamat && (
                  <div className="flex items-start gap-2 text-sm">
                    <MapPin
                      size={14}
                      className="text-muted-foreground mt-0.5"
                    />
                    <span className="flex-1">{item.alamat}</span>
                  </div>
                )}
                {item.nama_pemilik_toko && (
                  <p className="text-sm text-muted-foreground">
                    Pemilik Toko: <span className="font-medium text-foreground">{item.nama_pemilik_toko}</span>
                  </p>
                )}
                {normalizeProofImageUrls(item.foto_toko_list || item.foto_toko).length > 0 && (
                  <div className="space-y-2 pt-1">
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-sm font-medium text-foreground">Arsip Bukti Toko</p>
                      <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-800">
                        {normalizeProofImageUrls(item.foto_toko_list || item.foto_toko).length} foto
                      </span>
                    </div>
                    <ProofImageGallery
                      images={normalizeProofImageUrls(item.foto_toko_list || item.foto_toko).slice(0, 3).map((url, index) => {
                        const imageSrc = buildProofImageSrc(url);
                        return {
                          id: `${item.supplier_id}-foto-${index}`,
                          src: imageSrc,
                          href: imageSrc,
                          label: `Foto ${index + 1}`,
                          alt: `Foto toko ${index + 1}`,
                        };
                      })}
                      hint={normalizeProofImageUrls(item.foto_toko_list || item.foto_toko).length > 3 ? `+${normalizeProofImageUrls(item.foto_toko_list || item.foto_toko).length - 3} foto lainnya` : undefined}
                    />
                  </div>
                )}
              </div>

              {canManageSupplier ? (
                <div className="flex gap-2">
                  <Button
                    data-testid={`edit-supplier-${item.supplier_id}`}
                    variant="outline"
                    size="sm"
                    onClick={() => handleEdit(item)}
                    className="flex-1"
                  >
                    <Edit size={16} className="mr-2" />
                    Edit
                  </Button>
                  <Button
                    data-testid={`delete-supplier-${item.supplier_id}`}
                    variant="outline"
                    size="sm"
                    onClick={() => handleDelete(item.supplier_id)}
                  >
                    <Trash2 size={16} className="text-destructive" />
                  </Button>
                </div>
              ) : null}
            </div>
          ))
        )}
      </div>

      {canManageSupplier ? (
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editMode ? "Edit Supplier" : "Tambah Supplier"}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label>Nama Supplier</Label>
                <Input
                  ref={namaSupplierInputRef}
                  data-testid="form-nama-supplier"
                  value={formData.nama_supplier}
                  onChange={(e) =>
                    setFormData({ ...formData, nama_supplier: e.target.value })
                  }
                  required
                  onKeyDown={(event) =>
                    handleArrowFieldKeyDown(event, {
                      onLeft: () => supplierCancelButtonRef.current?.focus(),
                      onRight: () => focusFirstKategoriOrNik(),
                    })
                  }
                />
              </div>

              <div className="space-y-2">
                <Label>Kategori Supplier</Label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 rounded-md border p-3">
                  {kategori.length === 0 ? (
                    <p className="text-sm text-muted-foreground">Belum ada kategori aktif</p>
                  ) : (
                    kategori.map((kat, index) => {
                      const checked = formData.kategori_ids.includes(kat.kategori_id);
                      return (
                        <label key={kat.kategori_id} className="flex items-center gap-2 text-sm">
                          <Checkbox
                            ref={(node) => setKategoriCheckboxRef(kat.kategori_id, node)}
                            checked={checked}
                            onCheckedChange={(value) => {
                              const isChecked = value === true;
                              if (isChecked) {
                                if (formData.kategori_ids.includes(kat.kategori_id)) {
                                  return;
                                }
                                setFormData((prev) => ({
                                  ...prev,
                                  kategori_ids: [...prev.kategori_ids, kat.kategori_id],
                                }));
                              } else {
                                setFormData((prev) => ({
                                  ...prev,
                                  kategori_ids: prev.kategori_ids.filter(
                                    (id) => id !== kat.kategori_id
                                  ),
                                }));
                              }
                            }}
                            onKeyDown={(event) =>
                              handleArrowFieldKeyDown(event, {
                                onLeft: () => {
                                  if (index <= 0) {
                                    namaSupplierInputRef.current?.focus();
                                    return;
                                  }
                                  focusKategoriCheckboxByIndex(index - 1);
                                },
                                onRight: () => {
                                  if (index >= kategori.length - 1) {
                                    nikNpwpInputRef.current?.focus();
                                    return;
                                  }
                                  focusKategoriCheckboxByIndex(index + 1);
                                },
                              })
                            }
                          />
                          <span>{kat.nama_kategori}</span>
                        </label>
                      );
                    })
                  )}
                </div>
              </div>

              <div className="space-y-2">
                <Label>NIK/NPWP (Opsional)</Label>
                <Input
                  ref={nikNpwpInputRef}
                  data-testid="form-nik-npwp"
                  value={formData.nik_npwp}
                  onChange={(e) =>
                    setFormData({ ...formData, nik_npwp: e.target.value })
                  }
                  onKeyDown={(event) =>
                    handleArrowFieldKeyDown(event, {
                      onLeft: () => focusLastKategoriOrNama(),
                      onRight: () => namaPemilikTokoInputRef.current?.focus(),
                    })
                  }
                />
              </div>

              <div className="space-y-2">
                <Label>Nama Pemilik Toko (Opsional)</Label>
                <Input
                  ref={namaPemilikTokoInputRef}
                  data-testid="form-nama-pemilik-toko"
                  value={formData.nama_pemilik_toko}
                  onChange={(e) =>
                    setFormData({ ...formData, nama_pemilik_toko: e.target.value })
                  }
                  onKeyDown={(event) =>
                    handleArrowFieldKeyDown(event, {
                      onLeft: () => nikNpwpInputRef.current?.focus(),
                      onRight: () => teleponInputRef.current?.focus(),
                    })
                  }
                />
              </div>

              <div className="space-y-2">
                <Label>Telepon (Opsional)</Label>
                <Input
                  ref={teleponInputRef}
                  data-testid="form-telepon"
                  value={formData.telepon}
                  onChange={(e) =>
                    setFormData({ ...formData, telepon: e.target.value })
                  }
                  onKeyDown={(event) =>
                    handleArrowFieldKeyDown(event, {
                      onLeft: () => nikNpwpInputRef.current?.focus(),
                      onRight: () => alamatTextareaRef.current?.focus(),
                    })
                  }
                />
              </div>

              <div className="space-y-2">
                <Label>Alamat (Opsional)</Label>
                <Textarea
                  ref={alamatTextareaRef}
                  data-testid="form-alamat"
                  value={formData.alamat}
                  onChange={(e) =>
                    setFormData({ ...formData, alamat: e.target.value })
                  }
                  rows={3}
                  onKeyDown={(event) =>
                    handleArrowFieldKeyDown(event, {
                      onLeft: () => teleponInputRef.current?.focus(),
                      onRight: () => aktifCheckboxRef.current?.focus(),
                    })
                  }
                />
              </div>

              <div className="space-y-2">
                <Label>Status Supplier</Label>
                <label className="flex items-center gap-2 text-sm">
                  <Checkbox
                    ref={aktifCheckboxRef}
                    data-testid="form-aktif-supplier"
                    checked={formData.aktif}
                    onCheckedChange={(value) =>
                      setFormData((prev) => ({ ...prev, aktif: value === true }))
                    }
                    onKeyDown={(event) =>
                      handleArrowFieldKeyDown(event, {
                        onLeft: () => alamatTextareaRef.current?.focus(),
                        onRight: () => supplierCancelButtonRef.current?.focus(),
                      })
                    }
                  />
                  <span>Aktif</span>
                </label>
              </div>

              <div className="space-y-3 rounded-2xl border border-amber-200 bg-amber-50/50 p-4 shadow-sm">
                <div className="space-y-1">
                  <Label className="text-amber-950">Arsip Bukti Toko Fisik</Label>
                  <p className="text-xs text-amber-900/80">
                    Simpan foto papan nama, etalase, atau dokumen pendukung toko. Maksimal {MAX_SUPPLIER_TOKO_IMAGES} foto, masing-masing sampai 5MB.
                  </p>
                </div>

                <ProofImageDropzone
                  inputTestId="input-foto-toko"
                  tone="amber"
                  currentCount={normalizeProofImageUrls(formData.foto_toko_list).length + newFotoTokoEntries.length}
                  maxCount={MAX_SUPPLIER_TOKO_IMAGES}
                  title="Upload foto toko fisik"
                  description="Pilih beberapa foto sekaligus untuk arsip supplier. Gambar akan dikompres otomatis saat disimpan."
                  onFilesSelected={handleFotoTokoFilesSelected}
                />

                <ProofImageGallery
                  images={supplierFotoGalleryItems}
                  emptyText="Belum ada foto toko yang dipilih"
                  className="pt-1"
                />
              </div>

              <DialogFooter>
                <Button
                  ref={supplierCancelButtonRef}
                  type="button"
                  variant="outline"
                  onKeyDown={(event) =>
                    handleArrowFieldKeyDown(event, {
                      onLeft: () => aktifCheckboxRef.current?.focus(),
                      onRight: () => supplierSubmitButtonRef.current?.focus(),
                    })
                  }
                  onClick={() => setDialogOpen(false)}
                >
                  Batal
                </Button>
                <Button
                  ref={supplierSubmitButtonRef}
                  data-testid="submit-supplier-button"
                  type="submit"
                  onKeyDown={(event) =>
                    handleArrowFieldKeyDown(event, {
                      onLeft: () => supplierCancelButtonRef.current?.focus(),
                    })
                  }
                >
                  {editMode ? "Update" : "Simpan"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      ) : null}
      <ConfirmActionDialog
        open={confirmDialog.open}
        onOpenChange={(open) =>
          setConfirmDialog((prev) => ({ ...prev, open, onConfirm: open ? prev.onConfirm : null }))
        }
        title={confirmDialog.title}
        description={confirmDialog.description}
        confirmText={confirmDialog.confirmText}
        destructive={confirmDialog.destructive}
        onConfirm={async () => {
          if (confirmDialog.onConfirm) {
            await confirmDialog.onConfirm();
          }
          setConfirmDialog((prev) => ({ ...prev, open: false, onConfirm: null }));
        }}
      />
    </PageShell>
  );
};

export default MasterSupplierPage;
